import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Phone, Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const translations = {
  title: {
    sk: 'Kontaktujte nás',
    en: 'Contact Us',
    de: 'Kontaktieren Sie uns'
  },
  subtitle: {
    sk: 'Ozveme sa vám do 24 hodín',
    en: 'We will contact you within 24 hours',
    de: 'Wir melden uns innerhalb von 24 Stunden'
  },
  name: {
    sk: 'Meno',
    en: 'Name',
    de: 'Name'
  },
  namePlaceholder: {
    sk: 'Vaše meno',
    en: 'Your name',
    de: 'Ihr Name'
  },
  phone: {
    sk: 'Telefón',
    en: 'Phone',
    de: 'Telefon'
  },
  phonePlaceholder: {
    sk: '+421 900 123 456',
    en: '+1 234 567 890',
    de: '+49 123 456 789'
  },
  email: {
    sk: 'E-mail',
    en: 'Email',
    de: 'E-Mail'
  },
  emailPlaceholder: {
    sk: 'vas@email.sk',
    en: 'your@email.com',
    de: 'ihre@email.de'
  },
  submit: {
    sk: 'Odoslať',
    en: 'Send',
    de: 'Senden'
  },
  sending: {
    sk: 'Odosielam...',
    en: 'Sending...',
    de: 'Senden...'
  },
  successTitle: {
    sk: 'Odoslané!',
    en: 'Sent!',
    de: 'Gesendet!'
  },
  successMessage: {
    sk: 'Ozveme sa vám čo najskôr.',
    en: 'We will contact you soon.',
    de: 'Wir melden uns bald bei Ihnen.'
  },
  close: {
    sk: 'Zavrieť',
    en: 'Close',
    de: 'Schließen'
  },
  required: {
    sk: 'Povinné pole',
    en: 'Required field',
    de: 'Pflichtfeld'
  },
  invalidEmail: {
    sk: 'Neplatný e-mail',
    en: 'Invalid email',
    de: 'Ungültige E-Mail'
  }
};

const ContactModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', phone: '', email: '' });
      setErrors({});
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = translations.required[language];
    }
    if (!formData.email.trim()) {
      newErrors.email = translations.required[language];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = translations.invalidEmail[language];
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Use current origin for API calls (works for both preview and custom domain)
      const API_URL = window.location.origin;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone || null,
          email: formData.email
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send');
      }

      setIsSuccess(true);
    } catch (error) {
      console.error('Error sending contact form:', error);
      // Still show success to user (email might have been sent)
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="contact-modal-backdrop"
          />

          {/* Modal - Centered with flexbox */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="relative z-10 bg-white rounded-2xl shadow-2xl overflow-hidden w-[90%] max-w-md mx-4"
            data-testid="contact-modal"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#E8A238] to-[#D97706] px-6 py-4 relative">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                data-testid="contact-modal-close"
              >
                <X className="w-5 h-5 text-[#131132]" />
              </button>
              <h2 className="text-xl font-bold text-[#131132]" data-testid="contact-modal-title">
                {translations.title[language]}
              </h2>
              <p className="text-[#131132]/70 text-sm">
                {translations.subtitle[language]}
              </p>
            </div>

            {/* Content */}
            <div className="p-5">
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-6"
                  data-testid="contact-modal-success"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-[#131132] mb-1">
                    {translations.successTitle[language]}
                  </h3>
                  <p className="text-[#131132]/60 text-sm mb-4">
                    {translations.successMessage[language]}
                  </p>
                  <button
                    onClick={onClose}
                    className="px-5 py-2.5 bg-[#131132] text-white font-semibold rounded-xl hover:bg-[#131132]/90 transition-colors"
                    data-testid="contact-modal-close-success"
                  >
                    {translations.close[language]}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
                  {/* Name */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#131132] mb-1">
                      <User className="w-4 h-4" />
                      {translations.name[language]} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={translations.namePlaceholder[language]}
                      className={`w-full px-4 py-2.5 border-2 rounded-xl outline-none transition-colors
                        ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-[#E8A238]'}`}
                      data-testid="contact-form-name"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#131132] mb-1">
                      <Phone className="w-4 h-4" />
                      {translations.phone[language]}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={translations.phonePlaceholder[language]}
                      className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-[#E8A238] transition-colors"
                      data-testid="contact-form-phone"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#131132] mb-1">
                      <Mail className="w-4 h-4" />
                      {translations.email[language]} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={translations.emailPlaceholder[language]}
                      className={`w-full px-4 py-2.5 border-2 rounded-xl outline-none transition-colors
                        ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-[#E8A238]'}`}
                      data-testid="contact-form-email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
                      ${isSubmitting 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-[#131132] text-white hover:bg-[#131132]/90'}`}
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {translations.sending[language]}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {translations.submit[language]}
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
